import { applyVertexPositions } from '../core.js';
import { faceNormal } from '../edges.js';
import {
  REGION_NORMAL_EPSILON,
  REGION_PLANE_EPSILON,
  coplanarRegion,
} from '../regions.js';
import { cross3, dot3, sub3 } from '../vector-math.js';

// Same tightness rationale as INSET_AREA_EPSILON: a scaled-away triangle
// must come out genuinely flat, not "close enough". Deliberately compared
// against a LOCAL, per-face characteristic length (see the validation loop
// below), not a single region-wide edge — the same anisotropy mistake an
// earlier fix to Inset's own degeneracy check made and then corrected:
// deriving one shared scale from an unrelated long edge elsewhere in a
// region falsely rejects a legitimately thin-but-valid triangle elsewhere
// in that same region.
const SCALE_AREA_EPSILON = 1e-9;

/**
 * The one scale algorithm — scale an explicit, caller-chosen *set* of faces
 * uniformly within their own plane, in place, around the arithmetic centroid
 * of the set's own unique vertices. `scaleFace` is the single-face case;
 * `scaleRegion` discovers a connected coplanar region (via `coplanarRegion`,
 * the same discovery `extrudeRegion`/`insetRegion` use) and passes its face
 * ids here.
 *
 * Unlike extrude/inset, scale mints no new vertices or faces — it only
 * rewrites the existing region vertices' positions (via
 * `applyVertexPositions`, the same primitive `setVertexPositions` uses), so
 * every topology id involved is fully preserved, not just "where practical".
 * That also means it needs no boundary-loop discovery and has none of
 * insetFaceSet's "no interior vertex" restriction — a plain per-vertex
 * `center + (position - center) * factor` is well-defined for every vertex
 * in the set, boundary or interior alike.
 *
 * The center is always recomputed fresh from the *current* positions of the
 * set's own unique vertices (first-appearance order, same convention
 * `extrudeFaceSet`/`insetFaceSet` use for their own vertex ordering) — it is
 * never persisted, so undo/redo/replay never need to reproduce a stored
 * value, only the same deterministic function of whatever positions are
 * live at call time.
 *
 * A positive uniform scale around a fixed center is a similarity transform:
 * it cannot by itself flip a non-degenerate triangle's winding or collapse
 * its area to zero, so every face *in the scaled set* is guaranteed to keep
 * both its area and its exact outward direction, for any positive factor.
 * That guarantee does NOT automatically extend to every *attached* face
 * outside the scaled set — a wall or ring face that references one of the
 * set's vertex ids without every one of its own corners being in the set
 * (e.g. an extruded cap's side walls, or an inset region's border ring; see
 * `extrudeFaceSet`/`insetFaceSet`'s own doc comments). Every affected face —
 * in the scaled set or merely attached to it — gets a scale-relative
 * degeneracy check (did its area collapse); whether it *also* gets an
 * orientation check (did its cross product flip sign) depends on whether it
 * is coplanar with the scaled region's own plane:
 *
 * - A **coplanar** attachment (an inset ring, or any other face sharing a
 *   plane with the scaled region) can genuinely fold: tapering happens
 *   entirely within that one shared plane, so a large enough factor can
 *   push the moved corner across the line formed by the face's un-scaled
 *   corner(s), folding the triangle through itself — full area, a
 *   perfectly valid normal, just pointing the opposite way. A magnitude-only
 *   (degenerate-area) check cannot see this: the folded triangle is not
 *   degenerate, only inverted. This is the case the orientation check below
 *   exists to catch, and it is applied to every affected face classified as
 *   coplanar with the scaled region (using the same normal/plane tolerances
 *   `coplanarRegion` itself uses — `REGION_NORMAL_EPSILON`/
 *   `REGION_PLANE_EPSILON` — evaluated against each face's own pre-scale
 *   position, since a coplanar attachment stays in that exact plane
 *   throughout: the scale center is itself a point in that plane, being the
 *   arithmetic mean of coplanar vertices, so scaling around it never moves
 *   anything out of the plane).
 * - A **non-coplanar** attachment (an extrude wall) is degeneracy-safe: its
 *   un-scaled corners lie in the original (base) plane, its scaled corners
 *   move within the parallel, distinct cap plane, and a point confined to
 *   one of two parallel, non-intersecting planes can never become collinear
 *   with an edge confined to the other — so its cross product can never
 *   reach zero, for any positive factor. It is NOT, however, safe to
 *   compare against its own *prior* orientation the way a coplanar
 *   attachment's fold is detected: for an extrusion with any tangential
 *   (non-normal) component, a wall's cross product is an affine — not
 *   purely scaling — function of the factor, and a self-comparison can
 *   cross zero at an entirely ordinary factor even though the resulting
 *   geometry stays fully valid and manifold-consistent with the rest of the
 *   mesh (confirmed: forcing such a "rejected" result through and checking
 *   every shared-edge's traversal direction — the same invariant
 *   `extrudeFaceSet`'s own boundary discovery relies on — finds it clean in
 *   every case tested). So the orientation check below is skipped entirely
 *   for a non-coplanar attachment; only its degeneracy is checked, which
 *   the plane-parallelism argument above already proves is always safe.
 *
 * All-or-nothing, like `extrudeFaceSet`/`insetFaceSet`: every lookup,
 * validation, and position computation happens before anything is written
 * to `topology`, including a finiteness check on every computed position —
 * an extreme factor applied to large-magnitude coordinates can overflow to
 * ±Infinity, whose cross product is NaN and would otherwise silently defeat
 * the degeneracy check below (`NaN < threshold` is always false).
 */
function scaleFaceSet(topology, faceIds, factor, commandName = 'scaleFaceSet') {
  if (!Array.isArray(faceIds) || faceIds.length === 0) {
    throw new Error(`${commandName} requires a non-empty array of face ids`);
  }
  const uniqueFaceIds = new Set(faceIds);
  if (uniqueFaceIds.size !== faceIds.length) {
    throw new Error(`${commandName} received duplicate face ids`);
  }
  for (const id of faceIds) {
    if (!topology.faces.some((f) => f.id === id)) throw new Error(`unknown face id '${id}'`);
  }

  if (!Number.isFinite(factor) || factor <= 0) {
    throw new Error(`${commandName} requires a positive finite scale factor`);
  }
  if (factor === 1) {
    throw new Error(`${commandName} requires a scale factor other than 1 (a factor of 1 would not change anything)`);
  }

  const faces = topology.faces.filter((f) => uniqueFaceIds.has(f.id));

  // Reject a degenerate source before computing anything from it — the same
  // up-front check `subdivideFaceSet` makes, and the same one `scaleRegion`
  // already gets for free because `coplanarRegion` runs `faceNormal` on its
  // seed. `scaleFace` reaches here directly and never needs a normal of its
  // own (it scales vertices about their centroid), so without this it was the
  // one face operator that would silently mutate a zero-area face while every
  // sibling refused it. See the degenerate-input audit under docs/superpowers/
  // specs for the enumeration that found it.
  for (const face of faces) faceNormal(topology, face.id);

  const vertexIds = [...new Set(faces.flatMap((face) => face.vertexIds))];

  const positionById = new Map(topology.vertices.map((v) => [v.id, v.position]));
  const center = vertexIds
    .reduce((acc, id) => {
      const p = positionById.get(id);
      return [acc[0] + p[0], acc[1] + p[1], acc[2] + p[2]];
    }, [0, 0, 0])
    .map((c) => c / vertexIds.length);

  // Every computed position is checked for finiteness right here, before
  // anything else runs: an extreme factor on large-magnitude coordinates
  // can overflow to ±Infinity, and this is the only point that can still
  // report exactly which vertex overflowed.
  const newPositionById = new Map(
    vertexIds.map((id) => {
      const p = positionById.get(id);
      const newPosition = [
        center[0] + (p[0] - center[0]) * factor,
        center[1] + (p[1] - center[1]) * factor,
        center[2] + (p[2] - center[2]) * factor,
      ];
      if (!newPosition.every(Number.isFinite)) {
        throw new Error(`${commandName} factor would move vertex '${id}' to a non-finite position`);
      }
      return [id, newPosition];
    }),
  );

  const positionForValidation = (id) => newPositionById.get(id) ?? positionById.get(id);
  const scaledVertexIdSet = new Set(vertexIds);
  const affectedFaces = topology.faces.filter((face) => face.vertexIds.some((id) => scaledVertexIdSet.has(id)));

  // Reference plane for the scaled region itself, from its first face's own
  // pre-scale state — in practice the scaled set is always internally
  // coplanar (`scaleFace`: a single triangle; `scaleRegion`: guaranteed by
  // `coplanarRegion`), so one plane/normal describes the whole set. Used
  // below only to decide which affected faces are coplanar with the scaled
  // region and therefore get the orientation check (see the doc comment
  // above); a null `referenceNormal` (the scaled region's own first face
  // already degenerate — unreachable through any supported command) simply
  // means no affected face is treated as coplanar, falling back to the
  // degeneracy-only check every affected face still gets regardless.
  const referenceBefore = faces[0].vertexIds.map((id) => positionById.get(id));
  const referenceCross = cross3(sub3(referenceBefore[1], referenceBefore[0]), sub3(referenceBefore[2], referenceBefore[0]));
  const referenceCrossLength = Math.hypot(...referenceCross);
  const referenceNormal = referenceCrossLength < 1e-9 ? null : referenceCross.map((n) => n / referenceCrossLength);
  const referencePlanePoint = referenceBefore[0];

  // Degeneracy for every affected face, orientation only for those coplanar
  // with the scaled region — see the doc comment above for why the two
  // checks apply to different sets of faces.
  for (const face of affectedFaces) {
    const beforePositions = face.vertexIds.map((id) => positionById.get(id));
    const afterPositions = face.vertexIds.map(positionForValidation);
    const [beforeA, beforeB, beforeC] = beforePositions;
    const [afterA, afterB, afterC] = afterPositions;

    const beforeCross = cross3(sub3(beforeB, beforeA), sub3(beforeC, beforeA));
    const beforeCrossLength = Math.hypot(...beforeCross);
    // A face that was already degenerate before this operation touched it
    // is a pre-existing malformed-topology issue (unreachable through any
    // supported command — every construction path already validates
    // non-degeneracy), not something Scale created or can meaningfully
    // validate against; skip both checks for it rather than newly
    // rejecting geometry Scale did not break.
    if (beforeCrossLength < 1e-9) continue;

    const afterCross = cross3(sub3(afterB, afterA), sub3(afterC, afterA));
    const afterCrossLength = Math.hypot(...afterCross);

    // Local, per-face characteristic length: THIS face's own shortest edge
    // in its known-valid pre-scale state — not a region-wide edge, and not
    // the (possibly still-collapsing) post-scale state either, so the
    // threshold can't degrade in lockstep with the very failure it's
    // meant to catch. See SCALE_AREA_EPSILON's own comment.
    const shortestBeforeEdge = Math.min(
      Math.hypot(...sub3(beforeB, beforeA)),
      Math.hypot(...sub3(beforeC, beforeB)),
      Math.hypot(...sub3(beforeA, beforeC)),
    );
    const minCrossLength = SCALE_AREA_EPSILON * shortestBeforeEdge * shortestBeforeEdge;

    if (afterCrossLength < minCrossLength) {
      throw new Error(`${commandName} factor would make face '${face.id}' degenerate (zero area) — the result would be self-intersecting or collapsed`);
    }

    // Coplanar with the scaled region? Same test `coplanarRegion` itself
    // uses (normal alignment within REGION_NORMAL_EPSILON, every vertex
    // within REGION_PLANE_EPSILON of the reference plane), evaluated
    // against this face's own pre-scale position. Only a coplanar face can
    // actually fold through itself (see the doc comment above) — a
    // non-coplanar attachment (an extrude wall) is skipped here entirely,
    // since comparing it against its own prior orientation is not a valid
    // invariant for it (an oblique extrusion's wall can legitimately rotate
    // past a self-comparison "flip" at an ordinary factor without the
    // result ever becoming invalid).
    const beforeFaceNormal = beforeCross.map((n) => n / beforeCrossLength);
    const isCoplanarWithScaledRegion = referenceNormal
      && dot3(referenceNormal, beforeFaceNormal) >= 1 - REGION_NORMAL_EPSILON
      && beforePositions.every((p) => Math.abs(dot3(sub3(p, referencePlanePoint), referenceNormal)) < REGION_PLANE_EPSILON);

    if (isCoplanarWithScaledRegion && dot3(beforeCross, afterCross) < 0) {
      throw new Error(`${commandName} factor would flip face '${face.id}' to face the opposite direction — the result would fold through itself`);
    }
  }

  applyVertexPositions(topology, [...newPositionById.entries()]);

  return { faceIds: faces.map((f) => f.id), scaledVertexIds: vertexIds, center };
}

/**
 * Scale one triangular face by `factor` around its own centroid, in place.
 * See `scaleFaceSet` for the actual algorithm — this is the single-face
 * case, kept as its own export for symmetry with `extrudeFace`/`insetFace`.
 * For "scale the visible face this triangle is part of," see `scaleRegion`.
 */
export function scaleFace(topology, faceId, factor) {
  const outcome = scaleFaceSet(topology, [faceId], factor, 'scaleFace');
  return { faceId: outcome.faceIds[0], scaledVertexIds: outcome.scaledVertexIds, center: outcome.center };
}

/**
 * Scale the connected coplanar region containing `faceId` — the visible
 * face a human clicked on, not just the one triangle the click happened to
 * hit. See `coplanarRegion` for discovery and `scaleFaceSet` for the shared
 * scale algorithm; mirrors `extrudeRegion`/`insetRegion`'s composition of
 * the two.
 *
 * Unlike `extrudeRegion`/`insetRegion`, there is no single representative
 * "cap face id" to hand back for chaining: scale never replaces or renames
 * any face, so the same `faceId` the caller passed in is still valid
 * afterward and remains the right id to re-select or scale again.
 *
 * Same coplanar-rediscovery caveat `insetRegion` documents for itself
 * applies here when the target is an inset's inner face: since inset's ring
 * and inner cap stay coplanar (no crease — see `insetFaceSet`), seeding
 * `coplanarRegion` from the inner face rediscovers the *entire* original
 * flat area, ring included, and this scales that whole combined patch
 * around its own combined centroid rather than just the inner face within
 * an unscaled ring. Unlike `insetRegion`'s own re-chaining, this does not
 * error — scale has no boundary-only restriction — but it is very unlikely
 * to be the caller's intent. To scale just an inset's inner face (tapering
 * the ring, the same way `scaleRegion` naturally tapers an extruded cap's
 * walls), either inset with `insetFace` (a lone triangle has no ring
 * sibling to merge with) or address the specific inner-cap triangle id
 * directly with `scaleFace` instead of `scaleRegion`.
 */
export function scaleRegion(topology, faceId, factor, options) {
  const regionFaceIds = coplanarRegion(topology, faceId, options);
  const outcome = scaleFaceSet(topology, regionFaceIds, factor, 'scaleRegion');
  return { faceIds: outcome.faceIds, scaledVertexIds: outcome.scaledVertexIds, center: outcome.center };
}
